/**
 * Smart Docs Module Types
 * Entity-aware document editing with AI-powered entity detection
 */

import type { JSONContent } from '@tiptap/react'
import type { FacetType } from './interviewer'

// ============================================================================
// DOCUMENT TYPES
// ============================================================================

export interface SmartDocument {
  id: string
  title: string
  content: JSONContent | null  // Tiptap JSON format
  plainText: string            // Plain text for AI analysis

  // Entity tracking
  detectedEntities: DetectedEntity[]
  confirmedEntities: ConfirmedEntity[]

  // Metadata
  createdAt: string
  updatedAt: string
  lastAnalyzedAt?: string

  // Template reference
  templateId?: string
  hasUserContent?: boolean  // False when created from template, true when user edits

  // Organization
  projectId?: string
  tags?: string[]
}

// ============================================================================
// ENTITY DETECTION
// ============================================================================

export type EntityStatus = 'pending' | 'accepted' | 'rejected' | 'linked'

export interface DetectedEntity {
  id: string
  type: FacetType
  content: string              // Entity label/description
  evidence: string             // Supporting text from document
  confidence: number           // 0-1 AI confidence

  // Position in document (for highlighting)
  position?: {
    start: number
    end: number
  }

  // AI suggestions
  suggestedOntologyType?: string
  suggestedOntologyReason?: string

  // User decision
  status: EntityStatus
  decidedAt?: string
}

export interface ConfirmedEntity {
  id: string
  entityId: string             // Detected entity ID
  ontologyUri?: string         // Linked ontology entity URI
  ontologyType: string         // Entity type (Problem, Person, etc.)
  label: string

  // Source
  sourceEntityId: string       // DetectedEntity.id
  createdAt: string
  createdFrom: 'detection' | 'manual' | 'linked'
}

// ============================================================================
// SLASH COMMANDS
// ============================================================================

export interface SlashCommand {
  id: string
  name: string
  description: string
  icon: string
  shortcut: string
  entityType: string
  color: string
}

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    id: 'problem',
    name: 'Problem',
    description: 'A pain point or challenge',
    icon: '!',
    shortcut: '/problem',
    entityType: 'Problem',
    color: '#dc3545',
  },
  {
    id: 'person',
    name: 'Person',
    description: 'Link a stakeholder or user',
    icon: '@',
    shortcut: '/person',
    entityType: 'Person',
    color: '#4a90a4',
  },
  {
    id: 'decision',
    name: 'Decision',
    description: 'A choice that was made',
    icon: '↗',
    shortcut: '/decision',
    entityType: 'Decision',
    color: '#5c8a6b',
  },
  {
    id: 'risk',
    name: 'Risk',
    description: 'A potential threat or blocker',
    icon: '⚠',
    shortcut: '/risk',
    entityType: 'Risk',
    color: '#dc3545',
  },
  {
    id: 'opportunity',
    name: 'Opportunity',
    description: 'A potential win or opening',
    icon: '★',
    shortcut: '/opportunity',
    entityType: 'Opportunity',
    color: '#5c8a6b',
  },
  {
    id: 'action',
    name: 'Action Item',
    description: 'A task or next step',
    icon: '☐',
    shortcut: '/action',
    entityType: 'ActionItem',
    color: '#c9a227',
  },
  {
    id: 'constraint',
    name: 'Constraint',
    description: 'A limitation or requirement',
    icon: '⊘',
    shortcut: '/constraint',
    entityType: 'Constraint',
    color: '#8b5cf6',
  },
]

// ============================================================================
// ENTITY TYPE CONFIGURATION (for UI)
// ============================================================================

export interface EntityTypeConfig {
  type: string
  label: string
  icon: string
  color: string
  bgColor: string
  borderColor: string
}

export const ENTITY_TYPE_CONFIG: Record<string, EntityTypeConfig> = {
  Problem: {
    type: 'Problem',
    label: 'Problem',
    icon: '!',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)',
    borderColor: 'rgba(220, 53, 69, 0.2)',
  },
  Person: {
    type: 'Person',
    label: 'Person',
    icon: '@',
    color: '#4a90a4',
    bgColor: 'rgba(74, 144, 164, 0.1)',
    borderColor: 'rgba(74, 144, 164, 0.2)',
  },
  Decision: {
    type: 'Decision',
    label: 'Decision',
    icon: '↗',
    color: '#5c8a6b',
    bgColor: 'rgba(92, 138, 107, 0.1)',
    borderColor: 'rgba(92, 138, 107, 0.2)',
  },
  Risk: {
    type: 'Risk',
    label: 'Risk',
    icon: '⚠',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)',
    borderColor: 'rgba(220, 53, 69, 0.2)',
  },
  Opportunity: {
    type: 'Opportunity',
    label: 'Opportunity',
    icon: '★',
    color: '#5c8a6b',
    bgColor: 'rgba(92, 138, 107, 0.1)',
    borderColor: 'rgba(92, 138, 107, 0.2)',
  },
  ActionItem: {
    type: 'ActionItem',
    label: 'Action Item',
    icon: '☐',
    color: '#c9a227',
    bgColor: 'rgba(201, 162, 39, 0.1)',
    borderColor: 'rgba(201, 162, 39, 0.2)',
  },
  Constraint: {
    type: 'Constraint',
    label: 'Constraint',
    icon: '⊘',
    color: '#8b5cf6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    borderColor: 'rgba(139, 92, 246, 0.2)',
  },
  Stakeholder: {
    type: 'Stakeholder',
    label: 'Stakeholder',
    icon: '@',
    color: '#4a90a4',
    bgColor: 'rgba(74, 144, 164, 0.1)',
    borderColor: 'rgba(74, 144, 164, 0.2)',
  },
}

// Helper to get entity config with fallback
export function getEntityConfig(type: string): EntityTypeConfig {
  return ENTITY_TYPE_CONFIG[type] || {
    type,
    label: type,
    icon: '•',
    color: '#6c757d',
    bgColor: 'rgba(108, 117, 125, 0.1)',
    borderColor: 'rgba(108, 117, 125, 0.2)',
  }
}

// Display entity types (the 7 types we show in the filter)
export type DisplayEntityType =
  | 'Problem'
  | 'Person'
  | 'Decision'
  | 'Risk'
  | 'Opportunity'
  | 'ActionItem'
  | 'Constraint'

// Mapping from FacetType to DisplayEntityType
// This ensures all detected facets get proper styling
const FACET_TO_DISPLAY_MAP: Record<string, DisplayEntityType> = {
  // Problems & Pains category → Problem
  problem: 'Problem',
  current_behavior: 'Problem',
  workaround: 'Problem',
  willingness_to_pay: 'Problem',
  frequency_mention: 'Problem',
  severity_mention: 'Problem',

  // Decisions & Rationale category
  decision: 'Decision',
  alternative: 'Decision',
  rationale: 'Decision',
  constraint: 'Constraint',
  stakeholder: 'Person',
  dependency: 'Constraint',

  // Alignment & Communication category
  misalignment: 'Risk',
  information_gap: 'Risk',
  action_item: 'ActionItem',
  escalation_risk: 'Risk',
  communication_pattern: 'Person',

  // Business Context category
  business_context: 'Decision',
  market_trend: 'Opportunity',
  competitor_mention: 'Constraint',
  opportunity: 'Opportunity',
  risk: 'Risk',
  success_metric: 'Decision',
}

/**
 * Maps a FacetType or suggestedOntologyType to a DisplayEntityType
 * Use this for consistent styling across the Smart Docs module
 */
export function mapToDisplayEntityType(
  facetType?: string,
  suggestedOntologyType?: string
): DisplayEntityType {
  // Priority 1: If suggestedOntologyType matches a display type, use it
  if (suggestedOntologyType && ENTITY_TYPE_CONFIG[suggestedOntologyType]) {
    return suggestedOntologyType as DisplayEntityType
  }

  // Priority 2: Map facetType to display type
  if (facetType) {
    const mapped = FACET_TO_DISPLAY_MAP[facetType.toLowerCase()]
    if (mapped) return mapped
  }

  // Fallback: Problem
  return 'Problem'
}

// ============================================================================
// DETECTION STATE
// ============================================================================

export type DetectionStatus = 'idle' | 'analyzing' | 'complete' | 'error'

export interface DetectionState {
  status: DetectionStatus
  lastAnalyzedContent?: string
  error?: string
  detectedCount?: number
}
